import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import type { RecordWalker } from 'src/bitable/shared';
import { recordWalkerByCollectionViewId } from 'src/bitable/shared';
import { useFuSelector } from 'src/services/store-context/use-fine-context-selector';
import { useGetSortedRecordIds } from '../use-get-sorted-records';
import { EMPTY_GROUP_NAME } from './const';
import type { BiTableGroups } from './select-collection-groups';
import { selectCollectionGroups } from './select-collection-groups';

export const realGroupName = (groupName?: string) => {
  return groupName !== EMPTY_GROUP_NAME ? groupName : undefined;
};

export const useBiTableGroups = (viewId: string): BiTableGroups | undefined => {
  const sortedRecordIds = useGetSortedRecordIds(viewId);
  return useFuSelector(({ blocks, users, collectionViews }) => {
    return selectCollectionGroups({
      blocks,
      collectionViews,
      users,
      viewId,
      sortedRecordIds,
    });
  });
};

export const createRecordWalker = (
  findIndex: (recordId: string) => { records: string[]; index: number } | null
) => {
  return {
    checkRecord: (recordId: string) => {
      return findIndex(recordId) !== null;
    },
    prevRecord: (recordId: string) => {
      const result = findIndex(recordId);
      if (result == null) return null;
      const { records, index } = result;
      if (index <= 0) return null;
      return records[index - 1] ?? null;
    },
    nextRecord: (recordId: string) => {
      const result = findIndex(recordId);
      if (result == null) return null;
      const { records, index } = result;
      if (index >= records.length - 1) return null;
      return records[index + 1] ?? null;
    },
  };
};

export const useProvideRecordWalker = (viewId: string, walker: RecordWalker) => {
  const set = useSetAtom(recordWalkerByCollectionViewId);
  useEffect(() => {
    void set((table) => ({ ...table, [viewId]: walker }));
    return () => {
      void set((table) => {
        const { [viewId]: _walker, ...rest } = table;
        return rest;
      });
    };
  }, [set, viewId, walker]);
};

export const useBiTableGroupsAndProvideRecordWalker = (
  viewId: string,
  active = true
): BiTableGroups | undefined => {
  const groups = useBiTableGroups(viewId);
  const set = useSetAtom(recordWalkerByCollectionViewId);
  useEffect(() => {
    if (!active) return;
    const findIndex = (recordId: string) => {
      if (groups == null) return null;
      if (groups.withoutValidGroup) {
        const index = groups.sortedRecordIds.indexOf(recordId);
        if (index >= 0) return { records: groups.sortedRecordIds, index };
        return null;
      }
      for (const group of groups.visibleGroups) {
        const index = group.recordIds.indexOf(recordId);
        if (index >= 0) {
          return { records: group.recordIds, index };
        }
      }
      return null;
    };
    const walker = createRecordWalker(findIndex);
    void set((table) => ({ ...table, [viewId]: walker }));
    return () => {
      void set((table) => {
        const { [viewId]: _walker, ...rest } = table;
        return rest;
      });
    };
  }, [active, set, groups, viewId]);
  return groups;
};
